import { Link } from 'react-router-dom';
import type { NewsArticle } from '@/mocks/news';

interface NewsSectionProps {
  articles: NewsArticle[];
}

export default function NewsSection({ articles }: NewsSectionProps) {
  if (articles.length === 0) return null;
  const [lead, ...rest] = articles;

  return (
    <section className="w-full px-4 md:px-8">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950 flex items-center gap-2">
            <span className="w-1 h-6 rounded-full bg-accent-500" />
            影视资讯精选
          </h2>
          <p className="mt-1.5 text-sm text-foreground-600">行业动态、幕后故事与深度解读</p>
        </div>
        <Link
          to="/news"
          className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-accent-500 transition-colors"
        >
          更多资讯
          <i className="ri-arrow-right-line" />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
        <Link
          to={`/detail/${lead.id}`}
          className="group relative block overflow-hidden rounded-lg bg-secondary-100 aspect-[16/10] lg:aspect-auto lg:min-h-[340px]"
        >
          <img
            src={lead.cover}
            alt={`${lead.title} 资讯配图`}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
            <span className="inline-block rounded bg-accent-500 px-2 py-0.5 text-xs font-label font-semibold text-foreground-950">
              {lead.category}
            </span>
            <h3 className="mt-3 font-heading text-lg md:text-xl font-bold text-background-50 line-clamp-2 group-hover:text-accent-400 transition-colors">
              {lead.title}
            </h3>
            <p className="mt-2 text-sm text-background-200 line-clamp-2 hidden sm:block">{lead.summary}</p>
            <p className="mt-3 text-xs text-background-300 flex items-center gap-2">
              <i className="ri-time-line" />
              {lead.publishTime}
              <span>·</span>
              {lead.author}
            </p>
          </div>
        </Link>

        <div className="flex flex-col gap-3">
          {rest.slice(0, 4).map((article) => (
            <Link
              key={article.id}
              to={`/detail/${article.id}`}
              className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 hover:border-accent-400 hover:bg-accent-50/60 transition-colors"
            >
              <span className="w-24 h-16 md:w-28 md:h-[68px] shrink-0 overflow-hidden rounded-md bg-secondary-100">
                <img
                  src={article.cover}
                  alt={`${article.title} 资讯配图`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                  {article.title}
                </span>
                <span className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
                  <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-900">{article.category}</span>
                  <span className="font-label">{article.publishTime}</span>
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}